import { Box, Stack } from "@mui/material";
import TextField from "@mui/material/TextField";
import { BillingInformation, Contact } from "@repo/server-api";
import { BankAccountType } from "@repo/server-api/types";
import dayjs from "dayjs";
import { useTranslation } from "next-i18next";
import React, {
	useState,
	useEffect,
	useImperativeHandle,
	forwardRef,
	useCallback,
	useRef,
} from "react";
import * as Yup from "yup";

import Loader from "@repo/legacy/components/shared/Loader";

import DateField from "../fields/DateField";

const BankAccount = forwardRef(
	(
		props: Readonly<{
			contact?: Contact | null;
			setContact?: (value: Contact | null) => void;
			bankAccount?: BankAccountType;
			setBankAccount?: (value: BankAccountType) => void;
			errors?: Record<string, boolean | object | string>;
			setErrors?: (value: Record<string, boolean | object | string>) => void;
			isLoaded?: boolean;
		}>,
		ref: React.Ref<{
			validate: () => Promise<Record<string, boolean | object | string>>;
		}>,
	) => {
		const { t } = useTranslation();

		const [info, setInfo] = useState<BankAccountType>({
			bankName: "",
			iban: "",
			bic: "",
			clientReference: "",
			clientReferenceDate: null,
		});

		const [isLoaded, setIsLoaded] = useState<boolean>(false);

		useEffect(() => {
			if (props.contact && !isLoaded) {
				setIsLoaded(true);
				const bankInfo = props.contact.additionalInformation.filter(
					(info): info is BillingInformation =>
						(info as BillingInformation).__typename === "BillingInformation",
				)[0]?.bankAccount;
				setInfo(
					bankInfo ?? {
						bankName: "",
						iban: "",
						bic: "",
						clientReference: "",
						clientReferenceDate: null,
					},
				);
			}
			// TODO - fix this loading issue with better source
			if (
				props.bankAccount &&
				// (props.bankAccount.bankName !== info.bankName ||
				// 	props.bankAccount.iban !== info.iban ||
				// 	props.bankAccount.bic !== info.bic ||
				// 	props.bankAccount.clientReference !== info.clientReference)) ||
				// props.bankAccount.clientReferenceDate !== info.clientReferenceDate TODO import this line after fix of date handling
				!isLoaded
			) {
				setIsLoaded(true);
				setInfo(
					props.bankAccount ?? {
						bankName: "",
						iban: "",
						bic: "",
						clientReference: "",
						clientReferenceDate: null,
						id: "",
					},
				);
			}
		}, [props.contact, props.bankAccount, isLoaded, props.isLoaded]);

		useEffect(() => {
			if (
				props.bankAccount &&
				(props.bankAccount.bankName !== info.bankName ||
					props.bankAccount.iban !== info.iban ||
					props.bankAccount.bic !== info.bic ||
					props.bankAccount.clientReference !== info.clientReference ||
					props.bankAccount.clientReferenceDate !== info.clientReferenceDate) &&
				isLoaded
			) {
				props.setBankAccount?.(info);
			}
			if (props.setContact && props.contact && isLoaded)
				props.setContact({
					...props.contact,
					additionalInformation: props.contact.additionalInformation.map((infoItem) =>
						(infoItem as BillingInformation).__typename === "BillingInformation"
							? {
									...infoItem,
									bankAccount: {
										...info,
									},
								}
							: infoItem,
					),
				});
		}, [info, isLoaded]);

		const validationSchema = Yup.object().shape({
			bankName: Yup.string().required(t("bank-name-required", { ns: "validation" })),
			iban: Yup.string()
				.matches(/^[A-Z]{2}[0-9]{2}[A-Z0-9]{1,30}$/, t("iban-invalid-format", { ns: "validation" }))
				.required(t("iban-required", { ns: "validation" })),
			bic: Yup.string()
				.matches(
					/^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/,
					t("bic-invalid-format", { ns: "validation" }),
				)
				.required(t("bic-required", { ns: "validation" })),
		});

		const clientReferenceDateRef = useRef<{
			validate: () => Promise<Record<string, boolean | object | string>>;
		} | null>(null);

		const validateFields = useCallback(async () => {
			const localErrors: Record<string, boolean | object | string> = {};
			try {
				await validationSchema.validate(info, { abortEarly: false });
			} catch (err) {
				if (err instanceof Yup.ValidationError) {
					err.inner.forEach((error) => {
						if (error.path) {
							localErrors[error.path] = error.message;
						}
					});
				}
			}
			// localErrors.clientReferenceDate =
			// 	(await clientReferenceDateRef.current?.validate()) || {};

			props.setErrors?.({ ...localErrors });

			return localErrors;
		}, [info, props, validationSchema]);

		useImperativeHandle(ref, () => ({
			validate: validateFields,
		}));

		const handleFieldChange = (field: keyof BankAccountType, value: string) => {
			setInfo((prev: BankAccountType) => ({ ...prev, [field]: value }));
			props.setErrors?.({ ...props.errors, [field]: "" });
		};

		return (
			<div>
				{isLoaded ? (
					<Stack spacing={3}>
						<TextField
							onChange={(event) => handleFieldChange("bankName", event.target.value)}
							error={!!props.errors?.bankName}
							helperText={props.errors?.bankName as string}
							value={info.bankName ?? ""}
							label={t("bank-account.bank-name")}
							variant="outlined"
							fullWidth
						/>
						<Box sx={{ display: "flex", gap: 2 }}>
							<TextField
								onChange={(event) => handleFieldChange("iban", event.target.value)}
								error={!!props.errors?.iban}
								helperText={props.errors?.iban as string}
								value={info.iban ?? ""}
								label={t("bank-account.iban")}
								variant="outlined"
								sx={{ flex: 2 }}
							/>
							<TextField
								onChange={(event) => handleFieldChange("bic", event.target.value)}
								error={!!props.errors?.bic}
								helperText={props.errors?.bic as string}
								value={info.bic ?? ""}
								label={t("bank-account.bic")}
								variant="outlined"
								sx={{ flex: 1 }}
							/>
						</Box>
						<Box sx={{ display: "flex", gap: 2 }}>
							<TextField
								onChange={(event) => handleFieldChange("clientReference", event.target.value)}
								error={!!props.errors?.clientReference}
								helperText={props.errors?.clientReference as string}
								value={info.clientReference ?? ""}
								label={t("bank-account.client-reference")}
								variant="outlined"
								sx={{ flex: 1 }}
							/>
							<DateField
								isLoaded={isLoaded}
								ref={clientReferenceDateRef}
								value={info.clientReferenceDate ?? ""}
								setValue={(value) => {
									const dateValue =
										value !== "Invalid Date"
											? dayjs(value, "YYYY-MM-DD").format("YYYY-MM-DD")
											: null;

									setInfo({
										...info,
										clientReferenceDate: dateValue,
									});
								}}
								label={t("bank-account.client-reference-date")}
								errors={props.errors?.clientReferenceDate as Record<string, string>}
								setErrors={(value) =>
									props.setErrors &&
									props.setErrors({
										...props.errors,
										clientReferenceDate: value,
									})
								}
							/>
						</Box>
					</Stack>
				) : (
					<Loader />
				)}
			</div>
		);
	},
);

BankAccount.displayName = "BankAccount";
export default BankAccount;
